import PropTypes from 'prop-types'
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react'
import { TableHead } from '@components/ui/table'

const sortShape = PropTypes.shape({ key: PropTypes.string, dir: PropTypes.oneOf(['asc', 'desc']) })

const ariaSort = (sort, sortKey) => {
  if (sort?.key !== sortKey) return 'none'
  return sort.dir === 'asc' ? 'ascending' : 'descending'
}

/** A header label that sorts its column on click (asc → desc → default), with the direction icon. */
export const SortButton = ({ label, sortKey, sort, onSort }) => {
  const dir = sort?.key === sortKey ? sort.dir : null
  const Icon = dir === 'asc' ? ChevronUp : dir === 'desc' ? ChevronDown : ChevronsUpDown
  const next = dir === 'asc' ? 'descending' : dir === 'desc' ? 'default order' : 'ascending'
  return (
    <button
      type='button'
      onClick={() => onSort(sortKey)}
      className='inline-flex items-center gap-1 uppercase tracking-wide hover:opacity-80 focus:outline-none focus-visible:underline'
      aria-label={`Sort by ${label}, ${next}`}
      title={`Sort by ${label}`}
    >
      {label}
      <Icon
        className={`h-3.5 w-3.5 ${dir ? 'opacity-100' : 'opacity-50'}`}
        aria-hidden='true'
        data-testid={`sort-icon-${sortKey}-${dir || 'none'}`}
      />
    </button>
  )
}

SortButton.propTypes = {
  label: PropTypes.string.isRequired,
  sortKey: PropTypes.string.isRequired,
  sort: sortShape,
  onSort: PropTypes.func.isRequired
}

/** A queue table header whose whole label sorts the column. */
export const SortableTableHead = ({ label, sortKey, sort, onSort, className, title }) => (
  <TableHead className={className} title={title} aria-sort={ariaSort(sort, sortKey)}>
    <SortButton label={label} sortKey={sortKey} sort={sort} onSort={onSort} />
  </TableHead>
)

SortableTableHead.propTypes = {
  label: PropTypes.string.isRequired,
  sortKey: PropTypes.string.isRequired,
  sort: sortShape,
  onSort: PropTypes.func.isRequired,
  className: PropTypes.string,
  /** Header tooltip, e.g. what the column measures. */
  title: PropTypes.string
}
